import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Mail, Key, Send, CheckCircle, AlertCircle, ExternalLink, Shield, Settings, Info, Lock, UserPlus, Clock } from 'lucide-react';

const EmailMfaSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [settings, setSettings] = useState({
    resend_api_key: '',
    resend_api_key_masked: '',
    resend_api_key_set: false,
    resend_from_email: '',
    resend_from_name: 'C30 Dashboard',
    email_enabled: false,
    test_email_address: '',
    // Password Reset
    password_reset_enabled: false,
    password_reset_expiry_hours: 24,
    // New Account Notification
    new_account_notification_enabled: false,
    new_account_notification_email: ''
  });
  
  const [newApiKey, setNewApiKey] = useState('');
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSettings(response.data);
      setTestEmail(response.data.test_email_address || '');
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        resend_from_email: settings.resend_from_email,
        resend_from_name: settings.resend_from_name,
        email_enabled: settings.email_enabled,
        test_email_address: testEmail,
        // Password Reset
        password_reset_enabled: settings.password_reset_enabled,
        password_reset_expiry_hours: settings.password_reset_expiry_hours,
        // New Account Notification
        new_account_notification_enabled: settings.new_account_notification_enabled,
        new_account_notification_email: settings.new_account_notification_email
      };
      
      // Only include API key if a new one was entered
      if (newApiKey) {
        updateData.resend_api_key = newApiKey;
      }
      
      await axios.put(`${API}/admin/email-settings`, updateData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Email settings saved successfully');
      setNewApiKey(''); // Clear the input
      fetchSettings(); // Refresh to show updated masked key
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setTesting(true);
    try {
      const response = await axios.post(`${API}/admin/email-settings/test`, 
        { test_email: testEmail },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(response.data.message || 'Test email sent successfully');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-green-100 rounded-xl">
          <Mail className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Email & MFA Settings</h2>
          <p className="text-sm text-gray-500">Configure email delivery for MFA codes, password resets, and notifications</p>
        </div>
      </div>

      {/* Setup Instructions */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
            <Info className="w-5 h-5" />
            Setup Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-700 space-y-3">
          <p><strong>To enable email features, you need a Resend account:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Go to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline font-medium inline-flex items-center gap-1">resend.com <ExternalLink className="w-3 h-3" /></a> and create a free account</li>
            <li>In your Resend dashboard, go to <strong>API Keys</strong> and create a new key</li>
            <li>Copy the API key (starts with <code className="bg-blue-100 px-1 rounded">re_</code>) and paste it below</li>
            <li>To send from your own domain (required for production):
              <ul className="list-disc list-inside ml-4 mt-1">
                <li>Go to <strong>Domains</strong> in Resend dashboard</li>
                <li>Add your domain and configure the DNS records</li>
                <li>Update the "From Email" below to use your verified domain</li>
              </ul>
            </li>
            <li>Click <strong>Save Settings</strong>, then use <strong>Send Test Email</strong> to verify</li>
          </ol>
        </CardContent>
      </Card>

      {/* Resend API Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-orange-500" />
            Resend API Configuration
          </CardTitle>
          <CardDescription>
            Configure your Resend API credentials for sending emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* API Key Status */}
          <div className="flex items-center gap-2 p-3 rounded-lg border" style={{
            backgroundColor: settings.resend_api_key_set ? '#f0fdf4' : '#fef2f2',
            borderColor: settings.resend_api_key_set ? '#86efac' : '#fecaca'
          }}>
            {settings.resend_api_key_set ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-700 font-medium">API Key Configured</span>
                <code className="ml-2 text-sm bg-green-100 px-2 py-0.5 rounded text-green-800">
                  {settings.resend_api_key_masked}
                </code>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-700 font-medium">API Key Not Configured</span>
              </>
            )}
          </div>

          {/* API Key Input */}
          <div className="space-y-2">
            <Label htmlFor="api-key">
              {settings.resend_api_key_set ? 'Update API Key (leave blank to keep current)' : 'Resend API Key'}
            </Label>
            <Input
              id="api-key"
              type="password"
              placeholder="re_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxx"
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              className="font-mono"
            />
          </div>

          {/* From Email */}
          <div className="space-y-2">
            <Label htmlFor="from-email">From Email Address</Label>
            <Input
              id="from-email"
              type="email"
              placeholder="auth@yourdomain.com"
              value={settings.resend_from_email}
              onChange={(e) => setSettings({...settings, resend_from_email: e.target.value})}
            />
            <p className="text-xs text-gray-500">
              Use <code className="bg-gray-100 px-1 rounded">onboarding@resend.dev</code> for testing, or your verified domain email for production.
            </p>
          </div>

          {/* From Name */}
          <div className="space-y-2">
            <Label htmlFor="from-name">From Name</Label>
            <Input
              id="from-name"
              type="text"
              placeholder="C30 Dashboard"
              value={settings.resend_from_name}
              onChange={(e) => setSettings({...settings, resend_from_name: e.target.value})}
            />
          </div>
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-500" />
            Test Email Delivery
          </CardTitle>
          <CardDescription>
            Send a test email to verify your configuration is working
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-email">Test Email Address</Label>
            <div className="flex gap-2">
              <Input
                id="test-email"
                type="email"
                placeholder="your@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleTestEmail}
                disabled={testing || !settings.resend_api_key_set}
                variant="outline"
                className="gap-2"
              >
                {testing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
            {!settings.resend_api_key_set && (
              <p className="text-xs text-red-500">
                Configure and save your API key first before testing
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* MFA Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-500" />
            Multi-Factor Authentication (MFA)
          </CardTitle>
          <CardDescription>
            MFA settings are managed in User Admin. Email-based MFA uses the Resend configuration above.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">
              <strong>MFA Options Available:</strong>
            </p>
            <ul className="mt-2 space-y-1 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Authenticator App (Google Authenticator recommended)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Email Code (uses Resend API configured above)
              </li>
            </ul>
            <p className="mt-3 text-xs text-gray-500">
              Global MFA enforcement and user MFA status can be managed in Settings → User Admin
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-purple-500" />
            Password Reset
          </CardTitle>
          <CardDescription>
            Allow users to reset their password via email. This will also reset their MFA.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Enable Password Reset</Label>
              <p className="text-sm text-gray-500 mt-1">
                Users can request a password reset link via the login page
              </p>
            </div>
            <Switch
              checked={settings.password_reset_enabled}
              onCheckedChange={(checked) => setSettings({...settings, password_reset_enabled: checked})}
              disabled={!settings.resend_api_key_set}
            />
          </div>
          
          {!settings.resend_api_key_set && (
            <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Configure Resend API key above to enable password reset
            </p>
          )}
          
          {settings.password_reset_enabled && (
            <div className="space-y-2">
              <Label htmlFor="reset-expiry" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Reset Link Expiry (hours)
              </Label>
              <Input
                id="reset-expiry"
                type="number"
                min="1"
                max="168"
                value={settings.password_reset_expiry_hours}
                onChange={(e) => setSettings({...settings, password_reset_expiry_hours: parseInt(e.target.value) || 24})}
                className="w-32"
              />
              <p className="text-xs text-gray-500">
                How long the password reset link remains valid (1-168 hours)
              </p>
            </div>
          )}
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              <Info className="w-4 h-4 inline mr-2" />
              <strong>Note:</strong> When a user resets their password, their MFA is also reset. They will need to set up MFA again on their next login.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* New Account Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-indigo-500" />
            New Account Notifications
          </CardTitle>
          <CardDescription>
            Receive email notifications when new users register
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Enable Notifications</Label>
              <p className="text-sm text-gray-500 mt-1">
                Send an email when a new user creates an account
              </p>
            </div>
            <Switch
              checked={settings.new_account_notification_enabled}
              onCheckedChange={(checked) => setSettings({...settings, new_account_notification_enabled: checked})}
              disabled={!settings.resend_api_key_set}
            />
          </div>
          
          {!settings.resend_api_key_set && (
            <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Configure Resend API key above to enable notifications
            </p>
          )}
          
          {settings.new_account_notification_enabled && (
            <div className="space-y-2">
              <Label htmlFor="notification-email">Notification Email Address</Label>
              <Input
                id="notification-email"
                type="email"
                placeholder="admin@yourdomain.com"
                value={settings.new_account_notification_email}
                onChange={(e) => setSettings({...settings, new_account_notification_email: e.target.value})}
              />
              <p className="text-xs text-gray-500">
                This email will receive notifications when new users register
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          size="lg"
          className="gap-2"
          style={{ backgroundColor: '#ee7203' }}
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Save All Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EmailMfaSettings;
